import axios, { AxiosInstance } from 'axios';
import { getEnvVar } from '../config/env';

export type EnterpriseEventsResponse<T> = { success: boolean; data: T };

export type MigrationPhase = 'legacy' | 'shadow' | 'dual_write' | 'event_primary' | 'complete';
export type RecommendationStatus = 'pending' | 'approved' | 'applied' | 'rejected' | 'rolled_back';

class EnterpriseEventsService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${getEnvVar('VITE_FASTAPI_BASE_URL')}/api/v1/admin/enterprise-events`,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use((config) => {
      const token =
        localStorage.getItem('admin_auth_token') ||
        document.cookie.split('; ').find((r) => r.startsWith('auth_token='))?.split('=')[1];
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getStatus() {
    const { data } = await this.api.get<EnterpriseEventsResponse<Record<string, unknown>>>('/status');
    return data;
  }

  async getEventPlatform() {
    const { data } = await this.api.get<EnterpriseEventsResponse<Record<string, unknown>>>('/event-platform');
    return data;
  }

  async publishHealthPing() {
    const { data } = await this.api.post<EnterpriseEventsResponse<Record<string, unknown>>>('/event-platform/health-ping');
    return data;
  }

  async getOutbox() {
    const { data } = await this.api.get<EnterpriseEventsResponse<Record<string, unknown>>>('/outbox');
    return data;
  }

  async relayOutbox() {
    const { data } = await this.api.post<EnterpriseEventsResponse<Record<string, unknown>>>('/outbox/relay');
    return data;
  }

  async getDomains() {
    const { data } = await this.api.get<EnterpriseEventsResponse<unknown[]>>('/domains');
    return data;
  }

  async updateDomainPhase(domainKey: string, migrationPhase: MigrationPhase, notes?: string) {
    const { data } = await this.api.patch<EnterpriseEventsResponse<Record<string, unknown>>>(
      `/domains/${encodeURIComponent(domainKey)}/phase`,
      { migration_phase: migrationPhase, notes },
    );
    return data;
  }

  async getProjectors() {
    const { data } = await this.api.get<EnterpriseEventsResponse<Record<string, unknown>>>('/projectors');
    return data;
  }

  async runProjectors(limit = 50) {
    const { data } = await this.api.post<EnterpriseEventsResponse<Record<string, unknown>>>('/projectors/run', null, {
      params: { limit },
    });
    return data;
  }

  async getDbOptimization() {
    const { data } = await this.api.get<EnterpriseEventsResponse<Record<string, unknown>>>('/db-optimization');
    return data;
  }

  async updateRecommendationStatus(recommendationKey: string, status: RecommendationStatus) {
    const { data } = await this.api.patch<EnterpriseEventsResponse<Record<string, unknown>>>(
      `/db-optimization/recommendations/${encodeURIComponent(recommendationKey)}`,
      { status },
    );
    return data;
  }

  async getDeploymentDrain() {
    const { data } = await this.api.get<EnterpriseEventsResponse<Record<string, unknown>>>('/deployment/drain');
    return data;
  }

  async startDrain(targetVersion?: string) {
    const { data } = await this.api.post<EnterpriseEventsResponse<Record<string, unknown>>>('/deployment/drain/start', {
      target_version: targetVersion ?? null,
    });
    return data;
  }

  async completeDrain() {
    const { data } = await this.api.post<EnterpriseEventsResponse<Record<string, unknown>>>('/deployment/drain/complete');
    return data;
  }

  async cancelDrain() {
    const { data } = await this.api.post<EnterpriseEventsResponse<Record<string, unknown>>>('/deployment/drain/cancel');
    return data;
  }
}

export const enterpriseEventsService = new EnterpriseEventsService();
